import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LuCalendar,
  LuChevronLeft,
  LuCircleDollarSign,
  LuMail,
  LuMapPin,
  LuPhone,
  LuUser,
  LuUsers
} from "react-icons/lu";
import { MdCheckCircle } from "react-icons/md";
import ProgressBar from "./ProgressBar";
import {
  getPricingPeriod,
  getRoomRatesForDate,
  ROOMS_CATALOG
} from "./roomCatalog";
const parseInputDate = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
const Checkout = () => {
  const router = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    try {
      const raw = localStorage.getItem("bookingData");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setBookingDetails(parsed);
    } catch {
      setBookingDetails(null);
    } finally {
      setIsHydrated(true);
    }
  }, []);
  const guests = Number(searchParams.get("guests") ?? bookingDetails?.guests ?? "1");
  const room = useMemo(() => {
    const roomTypeParam = searchParams.get("roomType") ?? bookingDetails?.roomType;
    const resortId = searchParams.get("resortId") ?? bookingDetails?.resortId;
    const checkIn = searchParams.get("checkIn") ?? bookingDetails?.checkIn;
    const checkOut = searchParams.get("checkOut") ?? bookingDetails?.checkOut;
    if ((roomTypeParam !== "double" && roomTypeParam !== "junior-suite" && roomTypeParam !== "master-suite") || !checkIn || !checkOut) {
      return null;
    }
    const checkInDate = parseInputDate(checkIn);
    const checkOutDate = parseInputDate(checkOut);
    if (!checkInDate || !checkOutDate) {
      return null;
    }
    const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1e3 * 60 * 60 * 24));
    if (nights < 7) {
      return null;
    }
    const pricingPeriod = getPricingPeriod(checkInDate);
    if (!pricingPeriod) {
      return null;
    }
    const season = pricingPeriod.season;
    const roomType = roomTypeParam;
    const baseRoom = ROOMS_CATALOG.find((item) => item.type === roomType && (!resortId || item.resortId === resortId));
    if (!baseRoom) {
      return null;
    }
    const rateSet = getRoomRatesForDate(baseRoom, checkInDate);
    const pricePerNight = guests === 1 ? rateSet.single : rateSet.double;
    return {
      id: baseRoom.id,
      type: roomType,
      name: baseRoom.name,
      image: baseRoom.image,
      checkIn,
      checkOut,
      nights,
      season,
      totalPrice: pricePerNight * nights,
      periodLabel: pricingPeriod.label,
      rates: rateSet
    };
  }, [searchParams, bookingDetails, guests]);
  useEffect(() => {
    if (isHydrated && (!room || !bookingDetails)) {
      const roomType = searchParams.get("roomType");
      const resortId = searchParams.get("resortId");
      const checkIn = searchParams.get("checkIn");
      const checkOut = searchParams.get("checkOut");
      const guestCount = searchParams.get("guests");
      const params = new URLSearchParams();
      if (roomType) params.set("roomType", roomType);
      if (resortId) params.set("resortId", resortId);
      if (checkIn) params.set("checkIn", checkIn);
      if (checkOut) params.set("checkOut", checkOut);
      if (guestCount) params.set("guests", guestCount);
      const suffix = params.toString() ? `?${params.toString()}` : "";
      router(`/booking${suffix}`, { replace: true });
    }
  }, [isHydrated, room, bookingDetails, searchParams, router]);
  if (!isHydrated || !room || !bookingDetails) {
    return null;
  }
  const sendWhatsAppMessage = (paymentMethod) => {
    const bookingId = Math.floor(Math.random() * 1e8);
    const checkInDisplay = new Date(room.checkIn).toLocaleDateString();
    const checkOutDisplay = new Date(room.checkOut).toLocaleDateString();
    const guestsDisplay = `${guests} G\xE4ste`;
    const message = `*Neue Buchungsanfrage*

*Buchungs-ID:* ${bookingId}
*Zahlungsart:* ${paymentMethod}

*Gastinformationen*
Vorname/Nachname: ${bookingDetails.firstName} ${bookingDetails.lastName}
E-Mail: ${bookingDetails.email}
Telefon: ${bookingDetails.phone}
Adresse: ${bookingDetails.address}, ${bookingDetails.city}, ${bookingDetails.country} ${bookingDetails.zip}

*Buchungsdetails*
Zimmer: ${room.name}
Check-in: ${checkInDisplay}
Check-out: ${checkOutDisplay}
G\xE4ste: ${guestsDisplay}
Gesamtbetrag: EUR ${room.totalPrice.toFixed(2)}`;
    const whatsappUrl = `https://wa.me/94767744180?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };
  const handleCheckout = () => {
    setLoading(true);
    const paymentMethod = "Vorauszahlung per \xDCberweisung";
    sendWhatsAppMessage(paymentMethod);
    try {
      const raw = localStorage.getItem("bookingData");
      if (raw) {
        const parsed = JSON.parse(raw);
        localStorage.setItem(
          "bookingData",
          JSON.stringify({
            ...parsed,
            paymentMethod
          })
        );
      }
    } catch {
    }
    setTimeout(() => {
      setLoading(false);
      const params = new URLSearchParams({
        resortId: bookingDetails.resortId ?? "",
        roomType: room.type,
        checkIn: room.checkIn,
        checkOut: room.checkOut,
        guests: String(guests)
      });
      router(`/confirmation?${params.toString()}`);
    }, 1e3);
  };
  return <div className="min-h-screen bg-white">
    <section className="relative h-[54vh] min-h-[240px] overflow-hidden md:h-[40vh] lg:h-[76vh]">
      <img
        src={room.image}
        alt={`${room.name} Buchung`}
        className="object-cover" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} loading="eager" fetchPriority="high" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-800/70 to-transparent" />

      <div className="relative mx-auto flex h-full w-full max-w-7xl items-end px-4 pb-8 sm:px-6 lg:px-8 md:pb-10">
        <div className="max-w-3xl text-white">
          <span className="inline-flex rounded-full border border-white/35 bg-white/10 px-3 py-1 text-xs font-medium tracking-[0.12em] text-white/90 backdrop-blur-sm md:text-sm">
            Buchungsbestätigung
          </span>
          <h1 className="mt-4 text-3xl font-light leading-tight md:text-5xl">
            Buchungsdetails prüfen
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-white/90 md:text-base">
            Bitte prüfen Sie die Buchungsdetails für {room.name}.
          </p>
        </div>
      </div>
    </section>

    <div className="pt-4 md:pt-6">
      <ProgressBar currentStep="checkout" />
    </div>

    <div className="relative mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 md:py-12">
      <div className="pointer-events-none absolute -left-24 top-16 h-56 w-56 rounded-full bg-emerald-200/40 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 top-44 h-72 w-72 rounded-full bg-green-100/50 blur-3xl" />

      <div className="relative z-10 space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="overflow-hidden rounded-3xl border border-emerald-200 bg-white/95 p-6 shadow-[0_20px_50px_rgba(31,77,60,0.12)] backdrop-blur-sm md:p-8"
        >
          <div className="mb-6 flex flex-col gap-3 border-b border-emerald-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="flex items-center gap-2 text-2xl font-light text-gray-900 md:text-3xl">
              <MdCheckCircle className="h-7 w-7 text-[#1f4d3c]" /> Buchungsdetails
            </h2>
            <span className="w-fit rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] text-[#1f4d3c] md:text-sm">
              Prüfung
            </span>
          </div>

          <div className="space-y-4">
            {[
              { icon: LuUser, label: "Vorname", value: bookingDetails.firstName },
              { icon: LuUser, label: "Nachname", value: bookingDetails.lastName },
              { icon: LuMail, label: "E-Mail", value: bookingDetails.email },
              { icon: LuPhone, label: "Telefonnummer", value: bookingDetails.phone },
              { icon: LuMapPin, label: "Adresse", value: bookingDetails.address },
              { icon: LuMapPin, label: "Stadt", value: bookingDetails.city },
              { icon: LuMapPin, label: "Land", value: bookingDetails.country },
              { icon: LuMapPin, label: "Postleitzahl", value: bookingDetails.zip },
              { icon: LuCalendar, label: "Check-in", value: new Date(room.checkIn).toLocaleDateString() },
              { icon: LuCalendar, label: "Check-out", value: new Date(room.checkOut).toLocaleDateString() },
              { icon: LuUsers, label: "G\xE4ste", value: `${guests} Personen` }
            ].map((item) => <div
              key={item.label}
              className="flex flex-col rounded-xl border border-slate-100 bg-gradient-to-r from-white to-slate-50/70 px-4 py-3 transition-colors hover:border-emerald-200 hover:bg-emerald-50/40 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="mb-1 flex items-center gap-2 text-gray-600 sm:mb-0">
                <item.icon className="h-4 w-4 text-[#1f4d3c]" />
                <span className="text-sm font-medium md:text-base">{item.label}:</span>
              </div>
              <span className="break-words pl-6 text-sm font-medium text-gray-900 sm:pl-0 sm:text-right md:text-base">
                {item.value || "-"}
              </span>
            </div>)}

            <div className="mb-2 mt-5 rounded-2xl border border-slate-200 bg-[#f9fbff] p-5">
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.06em] text-gray-700">Preise: gültig von {room.periodLabel}</p>

              <div className="rounded-xl border border-amber-300 bg-amber-100/50 p-3">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-amber-700">Gültiger Preis</span>
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600">1 Person</span>
                    <span className="font-semibold text-gray-700">EUR {room.rates.single}/Nacht</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">2 Personen</span>
                    <span className="font-semibold text-gray-700">EUR {room.rates.double}/Nacht</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-1 flex flex-col gap-0.5 text-emerald-900 sm:mb-0">
                <div className="flex items-center gap-2">
                  <LuCircleDollarSign className="h-5 w-5 text-[#1f4d3c]" />
                  <span className="text-base font-bold md:text-lg">Gesamtbetrag</span>
                </div>
                <span className="pl-7 text-xs text-gray-600">
                  {room.season === "Summer" ? "Sommerpreise" : "Winterpreise"} · {guests === 1 ? "1 Person" : "2 Personen"}
                </span>
              </div>
              <span className="pl-6 text-lg font-bold text-[#1f4d3c] sm:pl-0 md:text-xl">
                EUR {room.totalPrice.toFixed(2)}
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="rounded-3xl border border-emerald-200 bg-white/95 p-6 shadow-[0_20px_50px_rgba(31,77,60,0.12)] backdrop-blur-sm md:p-8"
        >
          <div className="mt-6 grid grid-cols-1 gap-3 border-t border-gray-200 pt-6 sm:grid-cols-2 md:gap-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => router(-1)}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-800 shadow-sm transition-all duration-300 hover:bg-gray-100 md:py-4 md:text-base"
            >
              <LuChevronLeft className="h-5 w-5" />
              Zurück
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02, boxShadow: "0 10px 25px rgba(31,79,60,0.2)" }}
              whileTap={{ scale: 0.98 }}
              onClick={handleCheckout}
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#1f4d3c] to-[#c4a868] px-4 py-3 text-sm font-bold text-white shadow-lg transition-all duration-300 hover:from-[#173f2f] hover:to-[#b08d43] hover:shadow-xl disabled:from-emerald-300 disabled:to-amber-200 md:py-4 md:text-base"
            >
              {loading ? <>
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Wird verarbeitet...
              </> : <>
                <MdCheckCircle className="h-5 w-5" />
                Bestätigen und senden
              </>}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
  </div>;
};
export default Checkout;
